import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  adminApi,
  OrgUsageHealthRow,
  UsageHealthReport,
  UsageHealthStatus,
} from '../services/api';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';
import { getCurrentPeriod, getRecentPeriods, formatPeriodLabel } from '../utils/period';
import { ChipColor, getStatusMeta, POOL_LABELS } from '../utils/usage-status';
import { formatNum, formatPercent } from '../utils/format';
import { IS_BROKER_ADMIN } from '../config/admin-build';
import {
  effectiveBillingTag,
  isPayingBillingSignals,
  renderBillingTagChip,
} from '../components/BillingTagChip';

// Per-org context: an org with no run allocation reads "No allocation".
const STATUS_META = getStatusMeta('No allocation');

// The broker-AI "Billing" column + "Paying only" filter are gated on the
// broker-AI admin build: the backend-derived subscriptionTag is the self-serve
// paying signal (broker-AI's concern) — the sales-led IA tenant's paying status
// is manualBillingStatus, which the usage-health endpoint doesn't join, so
// showing the auto-derived tag on IA would mislead. (Trello #335)
//
// The build gate above is NOT sufficient on its own: this frontend and the
// backend subscriptionTag join ship from SEPARATE repos, so the admin can deploy
// to prod AHEAD of the backend. When that happens the report's rows carry no
// subscriptionTag, and a naive "Paying only" filter narrows everything out — a
// misleading empty "0 paying of N" list (the exact bug Marcus hit on #335). So
// the billing UI ALSO requires the backend to actually be supplying tags
// (hasBillingData) — otherwise it hides, and the page degrades to the plain
// usage-health view instead of the misleading empty state.

/**
 * True when the report carries live paying-status data — i.e. at least one org
 * row has a defined subscriptionTag. A report where NO row has a tag means the
 * backend isn't supplying billing data (e.g. it predates the #335 join, or the
 * frontend deployed ahead of it), so the Billing column + paying filter hide. An
 * all-'unpaid'/'trial' report still counts as billing-data-present — those are
 * defined tags, and 'no paying orgs' is then a truthful result, not a data gap.
 */
export function hasBillingData(rows: OrgUsageHealthRow[]): boolean {
  return rows.some((row) => row.subscriptionTag !== undefined);
}

/**
 * Filter to paying orgs when the toggle is on; else all rows.
 *
 * Judged on the row's EFFECTIVE billing tag (the shared `effectiveBillingTag`
 * the Organizations table uses), NOT the raw `subscriptionTag`. An EFT payer has
 * no Stitch subscription, so its raw tag is 'unpaid' — filtering on that dropped
 * every EFT-paying org from the "Paying only" list while the Organizations table
 * beside it correctly called them "Paid" (Trello #377).
 */
export function filterUsageRows(
  rows: OrgUsageHealthRow[],
  payingOnly: boolean,
): OrgUsageHealthRow[] {
  if (!payingOnly) return rows;
  return rows.filter((row) => isPayingBillingSignals(row));
}

/** Roll up per-status counts for the summary cards from the CURRENTLY-shown rows. */
export function countByStatus(rows: OrgUsageHealthRow[]): Record<UsageHealthStatus, number> {
  const counts: Record<UsageHealthStatus, number> = {
    healthy: 0,
    low: 0,
    critical: 0,
    alert: 0,
    unknown: 0,
  };
  for (const row of rows) counts[row.status] += 1;
  return counts;
}

const UsageHealthPage: React.FC = () => {
  const [period, setPeriod] = useState<string>(getCurrentPeriod());
  const [report, setReport] = useState<UsageHealthReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // "Paying only" filter (broker-AI build) — spot who's actively paying so the
  // sales team can find upgrade / top-up / low-adoption orgs. (Trello #335)
  const [payingOnly, setPayingOnly] = useState<boolean>(false);

  const periods = useMemo(() => getRecentPeriods(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getUsageHealth({ period })
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load usage health.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load usage health.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  // Billing UI (the "Billing" column + "Paying only" filter) shows only on the
  // broker-AI build AND when the backend is actually supplying paying-status
  // tags — so a frontend deployed ahead of the subscriptionTag backend degrades
  // to the plain usage view rather than a misleading empty "0 paying" list (#335).
  const showBilling = useMemo(
    () => IS_BROKER_ADMIN && hasBillingData(report?.orgs ?? []),
    [report],
  );
  // The base table has 7 columns; the broker-AI billing build adds "Billing" for 8.
  const columnCount = showBilling ? 8 : 7;
  // The paying filter is only active when the billing UI is shown — guards a
  // stale `payingOnly=true` if a report arrives without billing data.
  const payingActive = showBilling && payingOnly;

  // Rows currently shown: the report's orgs, narrowed to paying orgs when the
  // broker-AI "Paying only" toggle is on. The summary cards recompute from these
  // so the counts always match the table below.
  const visibleRows = useMemo(
    () => filterUsageRows(report?.orgs ?? [], payingActive),
    [report, payingActive],
  );
  const counts = useMemo(() => countByStatus(visibleRows), [visibleRows]);

  // The "running low" cards Marcus asked for: who needs an upgrade conversation.
  const cards: { key: UsageHealthStatus; caption: string; count: number }[] = [
    { key: 'alert', caption: 'Out of runs', count: counts.alert },
    { key: 'critical', caption: 'Critical (<10% left)', count: counts.critical },
    { key: 'low', caption: 'Running low (<20% left)', count: counts.low },
    { key: 'healthy', caption: 'Healthy', count: counts.healthy },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Usage Health
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Per-organisation run allocation vs consumption for the selected month. Spot orgs running
            low before they hit their cap. Usage is metered in runs (~200k tokens each).
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          {showBilling && (
            <FormControlLabel
              control={
                <Switch
                  checked={payingOnly}
                  onChange={(e) => setPayingOnly(e.target.checked)}
                  inputProps={{ 'data-testid': 'paying-only-toggle' } as any}
                />
              }
              label="Paying only"
            />
          )}
          <TextField
            select
            label="Period"
            size="small"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            sx={{ minWidth: 200 }}
            inputProps={{ 'data-testid': 'period-select' }}
          >
            {periods.map((p) => (
              <MenuItem key={p} value={p}>
                {formatPeriodLabel(p)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      <PageHelp {...PAGE_HELP.usageHealth} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            data-testid="summary-cards"
          >
            {cards.map((card) => {
              const meta = STATUS_META[card.key];
              const muiColor =
                meta.color === 'default' ? 'text.primary' : `${meta.color}.main`;
              return (
                <Card key={card.key} sx={{ flex: 1 }} data-testid={`card-${card.key}`}>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {card.caption}
                    </Typography>
                    <Typography
                      variant="h3"
                      sx={{ fontWeight: 700, mt: 1, color: muiColor }}
                      data-testid={`card-${card.key}-count`}
                    >
                      {card.count.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      organisation(s)
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" data-testid="org-table">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                    {showBilling && <TableCell sx={{ fontWeight: 600 }}>Billing</TableCell>}
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Allocated</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Used</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Remaining</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Consumed</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columnCount} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {payingActive
                            ? 'No paying organisations on this stack for this period.'
                            : 'No organisations on this stack for this period.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleRows.map((row) => {
                    // Fall back to 'unknown' if the backend ever returns a status
                    // outside the typed union — avoids a render crash on schema drift.
                    const meta = STATUS_META[row.status] ?? STATUS_META.unknown;
                    const barValue =
                      row.percentConsumed === null ? 0 : Math.min(row.percentConsumed, 100);
                    // meta.color is only 'default' for 'unknown' orgs, which always have
                    // percentConsumed === null and render '—' (no bar) below — so this
                    // 'warning' fallback just keeps a valid LinearProgress color and is
                    // never actually shown.
                    const barColor: ChipColor =
                      meta.color === 'default' ? 'warning' : meta.color;
                    const billingTag = effectiveBillingTag(row);
                    return (
                      <TableRow key={row.organizationId} hover data-testid={`row-${row.organizationId}`}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {row.organizationName || row.organizationId}
                          </Typography>
                          {row.organizationName && (
                            <Typography variant="caption" color="text.secondary">
                              {row.organizationId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.tierName || POOL_LABELS[row.poolModel]}
                          </Typography>
                          {row.seatCount !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              {row.seatCount} seat(s)
                            </Typography>
                          )}
                        </TableCell>
                        {showBilling && (
                          <TableCell data-testid={`billing-${row.organizationId}`}>
                            {/* The EFFECTIVE tag, not the raw subscriptionTag — an
                                EFT payer has no Stitch subscription and would
                                otherwise render "Unpaid" (Trello #377). */}
                            {billingTag ? (
                              renderBillingTagChip(billingTag)
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ textAlign: 'right' }}>{formatNum(row.runsAllocated)}</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>{row.runsUsed.toLocaleString()}</TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatNum(row.runsRemaining)}
                        </TableCell>
                        <TableCell>
                          {row.percentConsumed === null ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LinearProgress
                                variant="determinate"
                                value={barValue}
                                color={barColor}
                                sx={{ flex: 1, height: 8, borderRadius: 1 }}
                              />
                              <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'right' }}>
                                {formatPercent(row.percentConsumed)}
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              row.status === 'unknown'
                                ? 'No run allocation configured for this org'
                                : `${formatNum(row.runsRemaining)} of ${formatNum(row.runsAllocated)} runs remaining`
                            }
                          >
                            <Chip
                              size="small"
                              label={meta.label}
                              color={meta.color}
                              variant={meta.filled ? 'filled' : 'outlined'}
                              data-testid={`status-${row.organizationId}`}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {visibleRows.length.toLocaleString()}
            {payingActive ? ' paying ' : ' '}organisation(s)
            {payingActive ? ` of ${report.summary.totalOrgs.toLocaleString()}` : ''} · generated{' '}
            {new Date(report.generatedAt).toLocaleString()}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default UsageHealthPage;
